import { useState, useEffect } from 'react';
import { auth } from '@festpix/services';
import { getUserWallet, getWalletTransactions, getChildWallets, Transaction, Wallet } from '@festpix/services';
import { colors, spacing, typography } from '@festpix/ui';
import AttendeeLayout from '../../components/AttendeeLayout';

type WalletFilter = 'all' | string; // 'all' or wallet ID

interface WalletFilterOption {
  id: WalletFilter;
  label: string;
}

export default function TransactionsScreen() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parentWallet, setParentWallet] = useState<Wallet | null>(null);
  const [childWallets, setChildWallets] = useState<Wallet[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<WalletFilter>('all');

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const wallet = await getUserWallet(user.uid);
        if (wallet) {
          setParentWallet(wallet);

          // Fetch child wallets
          const children = await getChildWallets(wallet.id, user.uid);
          setChildWallets(children);

          // Fetch transactions for parent wallet
          const parentTxs = await getWalletTransactions(wallet.id);

          // Fetch transactions for all child wallets
          const childTxsPromises = children.map((child: Wallet) => getWalletTransactions(child.id));
          const childTxsArrays = await Promise.all(childTxsPromises);

          // Combine and deduplicate all transactions
          const allTxsMap = new Map<string, Transaction>();
          parentTxs.forEach((tx: Transaction) => allTxsMap.set(tx.id, tx));
          childTxsArrays.forEach((childTxs: Transaction[]) => {
            childTxs.forEach((tx: Transaction) => allTxsMap.set(tx.id, tx));
          });

          // Convert to array and sort by timestamp descending
          const allTxs = Array.from(allTxsMap.values());
          allTxs.sort((a, b) => {
            const dateA = 'toDate' in a.timestamp ? a.timestamp.toDate() : new Date(a.timestamp);
            const dateB = 'toDate' in b.timestamp ? b.timestamp.toDate() : new Date(b.timestamp);
            return dateB.getTime() - dateA.getTime();
          });

          // Filter out pending recharges - only show approved ones
          const filteredTxs = allTxs.filter(tx => {
            if (tx.type === 'recharge' && tx.status && tx.status !== 'approved') {
              return false;
            }
            return true;
          });
          setTransactions(filteredTxs);
        }
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  };

  const getFilterOptions = (): WalletFilterOption[] => {
    const options: WalletFilterOption[] = [
      { id: 'all', label: 'Tudo' },
    ];

    if (parentWallet) {
      options.push({ id: parentWallet.id, label: 'Principal' });
    }

    childWallets.forEach(child => {
      options.push({ id: child.id, label: child.childName || 'Filho' });
    });

    return options;
  };

  const getFilteredTransactions = (): Transaction[] => {
    if (selectedFilter === 'all') {
      return transactions;
    }

    return transactions.filter(tx =>
      tx.fromWalletId === selectedFilter || tx.toWalletId === selectedFilter
    );
  };

  const getTransactionTypeLabel = (tx: Transaction) => {
    const labels: Record<string, string> = {
      purchase: 'Compra',
      recharge: 'Recarga',
      transfer: 'Transferência',
    };
    const baseLabel = labels[tx.type] || tx.type.charAt(0).toUpperCase() + tx.type.slice(1);

    // Add payment method info for recharges
    if (tx.type === 'recharge' && tx.paymentMethod) {
      const methodLabels: Record<string, string> = {
        checkout_pro: 'Mercado Pago',
        pix: 'PIX',
        credit_card: 'Cartão',
      };
      const methodLabel = methodLabels[tx.paymentMethod] || tx.paymentMethod;
      return `${baseLabel} - ${methodLabel}`;
    }

    return baseLabel;
  };

  const formatDate = (timestamp: Transaction['timestamp']) => {
    if (!timestamp) return '';
    const date = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp);
    return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR')}`;
  };

  if (loading) {
    return (
      <AttendeeLayout showBackButton>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Carregando...</p>
        </div>
      </AttendeeLayout>
    );
  }

  const filterOptions = getFilterOptions();
  const filteredTransactions = getFilteredTransactions();
  const showFilters = childWallets.length > 0;

  return (
    <AttendeeLayout showBackButton>
      <div style={styles.container}>
        <h1 style={styles.title}>Historico de Transacoes</h1>

        {showFilters && (
          <div style={styles.filterContainer}>
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedFilter(option.id)}
                style={{
                  ...styles.filterTag,
                  ...(selectedFilter === option.id ? styles.filterTagActive : {}),
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {error && <p style={styles.errorText}>{error}</p>}

        <div style={styles.transactionsList}>
          {filteredTransactions.length === 0 ? (
            <p style={styles.emptyText}>Nenhuma transacao ainda</p>
          ) : (
            filteredTransactions.map((item) => {
              const isDebit = item.type === 'purchase';
              return (
                <div key={item.id} style={styles.transactionItem}>
                  <div style={styles.transactionInfo}>
                    <p style={styles.transactionType}>
                      {getTransactionTypeLabel(item)}
                    </p>
                    {item.boothName && (
                      <p style={styles.boothName}>{item.boothName}</p>
                    )}
                    <p style={styles.transactionDate}>
                      {formatDate(item.timestamp)}
                    </p>
                  </div>
                  <p style={{
                    ...styles.transactionAmount,
                    color: isDebit ? colors.error : colors.success
                  }}>
                    {isDebit ? '-' : '+'} R$ {item.amount.toFixed(2).replace('.', ',')}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AttendeeLayout>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
  },
  filterContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    padding: `${spacing.md}px ${spacing.md}px`,
    backgroundColor: colors.cardBackground,
    borderBottom: `1px solid ${colors.border}`,
  },
  filterTag: {
    padding: `${spacing.xs}px ${spacing.md}px`,
    borderRadius: 20,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    color: colors.textSecondary,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  filterTagActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    color: '#ffffff',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: colors.background,
  },
  spinner: {
    width: 40,
    height: 40,
    border: `3px solid ${colors.border}`,
    borderTop: `3px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: spacing.md,
    color: colors.textSecondary,
  },
  header: {
    padding: spacing.lg,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.cardBackground,
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: typography.fontSize.md,
    cursor: 'pointer',
    padding: 0,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    margin: 0,
  },
  transactionsList: {
    padding: 0,
  },
  transactionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.cardBackground,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionType: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs,
    color: colors.text,
    margin: 0,
  },
  boothName: {
    fontSize: typography.fontSize.sm,
    color: colors.info,
    marginBottom: spacing.xs,
    margin: 0,
  },
  transactionDate: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    margin: 0,
  },
  transactionAmount: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
  },
  emptyText: {
    textAlign: 'center',
    marginTop: spacing.xl + spacing.lg,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    padding: spacing.lg,
  },
  errorText: {
    color: colors.error,
    textAlign: 'center',
    padding: spacing.md,
    margin: 0,
  },
};
